import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIdentity } from './IdentityContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { GOLD, GOLD_LT, CREAM } from '../theme';
import DressImageManager from './DressImageManager';
import DressPreviewPane from './DressPreviewPane';
import PublishBar from './PublishBar';

const emptyForm = { name: '', unstitchedPrice: '', sizedPrice: '', description: '', details: [], note: '', shippingTime: '', announceNew: false };

const inputStyle = { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(201,162,75,.35)', color: CREAM, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, padding: '10px 2px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 };

export default function DressForm({ mode }) {
  const { categorySlug, dressSlug: routeSlug } = useParams();
  const { authFetch } = useIdentity();
  const toast = useToast();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null); // parent category (for name + preview context)
  const [dress, setDress] = useState(null); // full draft dress incl. images (edit mode only)
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return authFetch('/.netlify/functions/categories-draft')
      .then((r) => r.json())
      .then((doc) => {
        const foundCategory = (doc.categories || []).find((c) => c.slug === categorySlug);
        if (!foundCategory) {
          setNotFound(true);
          return;
        }
        setCategory(foundCategory);
        if (mode === 'edit') {
          const foundDress = (foundCategory.dresses || []).find((d) => d.slug === routeSlug);
          if (!foundDress) {
            setNotFound(true);
            return;
          }
          setDress(foundDress);
          setForm({
            name: foundDress.name,
            unstitchedPrice: foundDress.unstitchedPrice != null ? String(foundDress.unstitchedPrice) : '',
            sizedPrice: foundDress.sizedPrice != null ? String(foundDress.sizedPrice) : '',
            description: foundDress.description || '',
            details: foundDress.details || [],
            note: foundDress.note || '',
            shippingTime: foundDress.shippingTime || '',
            announceNew: Boolean(foundDress.announceNew),
          });
        } else {
          // The component instance can be reused across route changes
          // (e.g. navigating from editing one piece straight into "add a
          // new piece") without remounting, so `form` must be explicitly
          // reset here — otherwise the previous piece's values, including
          // a checked "announce new", silently carry over into the new one.
          setDress(null);
          setForm(emptyForm);
        }
      })
      .finally(() => setLoading(false));
  }, [authFetch, mode, categorySlug, routeSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleAnnounceNew = (e) => setForm((f) => ({ ...f, announceNew: e.target.checked }));
  const updateDetail = (i, value) => setForm((f) => ({ ...f, details: f.details.map((d, idx) => (idx === i ? value : d)) }));
  const addDetail = () => setForm((f) => ({ ...f, details: [...f.details, ''] }));
  const removeDetail = (i) => setForm((f) => ({ ...f, details: f.details.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSaveError('Name is required');
      return;
    }
    if (form.unstitchedPrice.trim() && (!Number.isFinite(Number(form.unstitchedPrice)) || Number(form.unstitchedPrice) <= 0)) {
      setSaveError('Unstitched price must be empty or a number greater than 0');
      return;
    }
    if (form.sizedPrice.trim() && (!Number.isFinite(Number(form.sizedPrice)) || Number(form.sizedPrice) <= 0)) {
      setSaveError('Size price must be empty or a number greater than 0');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      ...form,
      unstitchedPrice: form.unstitchedPrice.trim() ? Number(form.unstitchedPrice) : null,
      sizedPrice: form.sizedPrice.trim() ? Number(form.sizedPrice) : null,
    };
    try {
      if (mode === 'new') {
        const res = await authFetch('/.netlify/functions/categories-dresses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categorySlug, ...payload }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to create piece');
        toast.success('Piece created.');
        navigate(`/admin/categories/${categorySlug}/dresses/${data.dress.slug}`);
        return;
      }
      const res = await authFetch('/.netlify/functions/categories-dresses', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug, dressSlug: routeSlug, ...payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setDress(data.dress);
      toast.success('Draft saved.');
    } catch (err) {
      toast.error(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: 'rgba(239,228,210,.6)' }}>Loading…</p>;
  if (notFound) return <p style={{ color: '#e08a8a' }}>Piece not found in the draft.</p>;

  const previewDress = {
    slug: routeSlug || 'preview',
    ...form,
    unstitchedPrice: form.unstitchedPrice.trim() ? Number(form.unstitchedPrice) : null,
    sizedPrice: form.sizedPrice.trim() ? Number(form.sizedPrice) : null,
    images: dress ? dress.images : [],
  };

  return (
    <div>
      <Link to={`/admin/categories/${categorySlug}`} style={{ display: 'inline-block', marginBottom: 16, fontSize: 13, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, textDecoration: 'none' }}>← {category ? category.name : 'Category'}</Link>
      <h1 style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(26px,3.2vw,36px)', margin: '0 0 24px' }}>
        {mode === 'new' ? 'New Piece' : form.name || routeSlug}
      </h1>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0, maxWidth: 480 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={form.name} onChange={updateField('name')} />
          </div>
          {mode === 'edit' && (
            <div style={{ marginBottom: 20, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Slug: {routeSlug} (locked)</div>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Unstitched Price (PKR)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" placeholder='Leave empty to hide this option' value={form.unstitchedPrice} onChange={updateField('unstitchedPrice')} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Size Price (PKR)</label>
            <input style={inputStyle} type="number" min="0" step="0.01" placeholder='Leave empty to hide this option' value={form.sizedPrice} onChange={updateField('sizedPrice')} />
          </div>
          {!form.unstitchedPrice.trim() && !form.sizedPrice.trim() && (
            <p style={{ marginTop: -10, marginBottom: 20, fontSize: 12, color: 'rgba(239,228,210,.45)' }}>Leaving both empty shows "Enquire" instead of a price, same as an unpriced collection.</p>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.description} onChange={updateField('description')} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Details</label>
            {form.details.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} value={d} onChange={(e) => updateDetail(i, e.target.value)} />
                <button onClick={() => removeDetail(i)} style={{ background: 'none', border: 'none', color: '#e08a8a', cursor: 'pointer', fontSize: 18 }}>×</button>
              </div>
            ))}
            <button onClick={addDetail} style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: GOLD_LT, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add detail</button>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Note (optional)</label>
            <input style={inputStyle} value={form.note} onChange={updateField('note')} placeholder="e.g. Actual colours may vary slightly" />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Shipping time (optional, only shown when priced)</label>
            <input style={inputStyle} value={form.shippingTime} onChange={updateField('shippingTime')} placeholder="e.g. 3–4 weeks" />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: CREAM, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.announceNew} onChange={toggleAnnounceNew} />
              Show "What's New" popup after publish
            </label>
            <p style={{ margin: '6px 0 0 26px', fontSize: 12, color: 'rgba(239,228,210,.45)' }}>Replaces whatever announcement is currently active. Only one is shown at a time.</p>
          </div>

          {saveError && <p style={{ color: '#e08a8a', fontSize: 14 }}>{saveError}</p>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '14px 32px', background: GOLD, color: '#16110c', border: 'none', cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1 }}
          >
            {saving && <Spinner size={14} />}
            {saving ? 'Saving…' : 'Save Draft'}
          </button>

          {mode === 'edit' && dress && (
            <div style={{ marginTop: 40 }}>
              <DressImageManager categorySlug={categorySlug} dress={dress} onChange={setDress} />
            </div>
          )}

          {mode === 'new' && (
            <p style={{ marginTop: 24, fontSize: 13, color: 'rgba(239,228,210,.45)' }}>Save the piece first to start adding images.</p>
          )}
        </div>

        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          {category && <DressPreviewPane category={category} dress={previewDress} />}
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <PublishBar endpoint="/.netlify/functions/categories-publish" />
      </div>
    </div>
  );
}
